import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { Address, TextNodeType } from '@mwe/models';
import { DetailsLinkTypeEnum } from '@mwe/constants';
import { DetailsLinkTypeEnumExporter, getAddressGroupFormatted, isArrayWithMinOneItem, isCancelledRelocationOrMoveOut } from '@mwe/utils';
import { TooltipCardComponent } from '../tooltip-card/tooltip-card.component';
import { NgbDropdown, NgbDropdownButtonItem, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle } from '@ng-bootstrap/ng-bootstrap';
import { NgClass } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { InfoboxComponent } from '../infobox/infobox.component';

export interface AddressDropdownItem {
  id: string;
  label: string;
  disabled?: boolean;
}

export type DropdownType = 'pen' | 'dropdown' | 'custom' | 'dots';

@Component({
  selector: 'mwe-address',
  templateUrl: './address.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TooltipCardComponent,
    NgbDropdown,
    NgbDropdownToggle,
    NgClass,
    NgbDropdownMenu,
    NgbDropdownButtonItem,
    NgbDropdownItem,
    TranslatePipe,
    InfoboxComponent,
  ],
})
export class AddressComponent extends DetailsLinkTypeEnumExporter {
  onItemClick = output<string>();

  addressIdx = input<number | string>();
  dropdownType = input<DropdownType>();
  address = input.required<Address>();
  allowedUZorAZTooltipAddresses = input<string[]>(undefined);
  dropdownEditKey = input<string>();
  allDropdownItems = input<AddressDropdownItem[]>([]); // is split in featuredButtons and dropdownItems
  featuredBtnIds = input<string[]>([]);
  orderDateFormatted = input<string>();
  recentOrderMsgKey = input<string>();
  isArrowDownHidden = input<boolean>(false);
  isAddressDetailsBodyVisible = input<boolean>(false);
  productCategories = input<string[]>([]);
  singleMarker = input(false);
  textNodeType = input<TextNodeType>();
  isEndedContract = input<boolean>();
  hideTooltipBody = input<boolean>(false);

  addressHash = computed(() => this.address()?.hashCode);
  dropdownEditId = computed<string>(() => `mwe-address-${this.addressIdx()}-dropdown`);
  formattedAddress = computed<string>(() => getAddressGroupFormatted(this.address()));

  tooltipClass = computed<{ [value: string]: boolean }>(() => ({
    single: this.singleMarker(),
    'no-after': !!this.orderDateFormatted() || this.isArrowDownHidden(),
    'has-body':
      (!!this.address().addressGroup.detailsLinkType && this.isAddressDetailsBodyVisible()) || this.orderDateFormatted()?.length > 0,
  }));

  detailsLinkItemKey = computed<string>(() => {
    if (
      this.address().addressGroup.detailsLinkType === DetailsLinkTypeEnum.RELOCATION ||
      this.address().addressGroup.detailsLinkType === DetailsLinkTypeEnum.RELOCATION_ERROR
    ) {
      return 'relocation.status';
    }

    if (
      this.address().addressGroup.detailsLinkType === DetailsLinkTypeEnum.MOVEOUT ||
      this.address().addressGroup.detailsLinkType === DetailsLinkTypeEnum.MOVEOUT_IGNORE_ERROR
    ) {
      return 'moveout.status';
    }

    return '';
  });

  featuredButtons = computed<AddressDropdownItem[]>(() => {
    return this.allDropdownItems()?.filter(i => this.hasId(i.id));
  });

  dropdownItems = computed<AddressDropdownItem[]>(() => {
    if (!isArrayWithMinOneItem(this.featuredBtnIds())) {
      return this.allDropdownItems() || [];
    }

    return this.allDropdownItems().filter(i => !this.hasId(i.id));
  });

  hasTooltipBody = computed<boolean>(() => {
    return (
      ((this.address().addressGroup.detailsLinkType &&
        !isCancelledRelocationOrMoveOut(this.address().addressGroup.detailsLinkType) &&
        this.isAddressDetailsBodyVisible()) ||
        this.orderDateFormatted()?.length > 0) &&
      !this.isEndedContract() &&
      (this.allowedUZorAZTooltipAddresses()?.some(hash => this.addressHash() === hash) ?? true)
    );
  });

  shouldDisplayActionComponents = computed<boolean>(() => {
    return !this.isEndedContract() && this.hasDisplayableActionComponents();
  });

  constructor() {
    super();
  }

  executeItemAction(id: string): void {
    this.onItemClick.emit(id);
  }

  private hasDisplayableActionComponents(): boolean {
    return this.featuredButtons().length > 0 || !!this.dropdownType();
  }

  private hasId(id: string) {
    return !!this.featuredBtnIds().find(fId => fId === id);
  }
}
